import { useState } from 'react';
import { cn } from '../../../../../../lib/utils';
import { primaryText, secondaryText, semiBoldText, smallTextSize } from '../../../../../../styling/stylingHelper';
import type { RevenueCheckResult, RevenueCheckEmailFinding } from '../../../../../../services/admin/adminService';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { TrivecFindingBlock, TrivecSummaryChip } from './panelPrimitives';
import type { TFn } from '../../types';

/** All five finding types for one qualified email. */
function TrivecFindingCard({ finding, t, eur, overridden }: { finding: RevenueCheckEmailFinding; t: TFn; eur: (n: number) => string; overridden: boolean }) {
  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 space-y-2">
      <div className="min-w-0">
        <div className={cn(primaryText, semiBoldText, smallTextSize, 'truncate')}>{finding.subject}</div>
        <div className={cn(secondaryText, 'text-xs')}>{t('adminTesting.revenueCheck.reportDate', { date: finding.reportDate })}</div>
        <div className={cn(secondaryText, 'text-xs font-mono truncate opacity-70')} title={finding.rfc822MessageId}>{finding.rfc822MessageId}</div>
      </div>

      {finding.unconfiguredRestaurants.length > 0 && (
        <TrivecFindingBlock tone="info" title={t('adminTesting.revenueCheck.findingUnconfiguredRestaurants', { count: finding.unconfiguredRestaurants.length })}>
          {finding.unconfiguredRestaurants.map((r, i) => (
            <li key={i} className={cn(secondaryText, 'text-xs')}>{r.reportName} — {eur(r.totalValue)}</li>
          ))}
        </TrivecFindingBlock>
      )}

      {finding.missingDays.length > 0 && (
        <TrivecFindingBlock tone="warn" title={t('adminTesting.revenueCheck.findingMissingDays', { count: finding.missingDays.length })}>
          {finding.missingDays.map((d, i) => (
            <li key={i} className={cn(secondaryText, 'text-xs')}>{d.restaurantName}</li>
          ))}
        </TrivecFindingBlock>
      )}

      {finding.missingSections.length > 0 && (
        <TrivecFindingBlock tone="warn" title={t('adminTesting.revenueCheck.findingMissingSections', { count: finding.missingSections.length })}>
          {finding.missingSections.map((m, i) => (
            <li key={i} className={cn(secondaryText, 'text-xs')}>
              <span className={cn(primaryText, 'font-medium')}>{m.restaurantName}</span>: {m.sections.map((s) => `${s.name} (${eur(s.value)})`).join(', ')}
            </li>
          ))}
        </TrivecFindingBlock>
      )}

      {finding.valueMismatches.length > 0 && (
        <TrivecFindingBlock tone="error" title={t(overridden ? 'adminTesting.revenueCheck.findingValueMismatchesApplied' : 'adminTesting.revenueCheck.findingValueMismatches', { count: finding.valueMismatches.length })}>
          {finding.valueMismatches.map((v, i) => (
            <li key={i} className={cn(secondaryText, 'text-xs')}>
              <span className={cn(primaryText, 'font-medium')}>{v.restaurantName}</span> · {v.section}: {eur(v.storedValue)} → {eur(v.reportValue)}
              {v.storedQuantity !== undefined && v.storedQuantity !== v.reportQuantity && (
                <> · {t('adminTesting.revenueCheck.unitsChange', { stored: v.storedQuantity, report: v.reportQuantity })}</>
              )}
            </li>
          ))}
        </TrivecFindingBlock>
      )}

      {finding.unconfiguredSections.length > 0 && (
        <TrivecFindingBlock tone="info" title={t('adminTesting.revenueCheck.findingUnconfiguredSections', { count: finding.unconfiguredSections.length })}>
          {finding.unconfiguredSections.map((u, i) => (
            <li key={i} className={cn(secondaryText, 'text-xs')}>
              <span className={cn(primaryText, 'font-medium')}>{u.restaurantName}</span>: {u.sections.map((s) => `${s.name} (${eur(s.value)})`).join(', ')}
            </li>
          ))}
        </TrivecFindingBlock>
      )}
    </div>
  );
}

/**
 * Aggregate-first worklist — the PRIMARY operator view. Rolls up all five finding types across
 * every email (per restaurant / section, with days affected + total value) so a high-volume run
 * reads as a short actionable list instead of dozens of repeated per-email lines.
 */
function TrivecAggregateView({ agg, t, eur, overridden }: { agg: RevenueCheckResult['aggregate']; t: TFn; eur: (n: number) => string; overridden: boolean }) {
  return (
    <div className="space-y-2">
      {agg.unconfiguredRestaurants.length > 0 && (
        <TrivecFindingBlock tone="info" title={t('adminTesting.revenueCheck.findingUnconfiguredRestaurants', { count: agg.unconfiguredRestaurants.length })}>
          {agg.unconfiguredRestaurants.map((r, i) => (
            <li key={i} className={cn(secondaryText, 'text-xs')}>
              {t('adminTesting.revenueCheck.aggUnconfiguredRestaurant', { restaurant: r.reportName, days: r.days, value: eur(r.totalValue) })}
            </li>
          ))}
        </TrivecFindingBlock>
      )}

      {agg.missingDays.length > 0 && (
        <TrivecFindingBlock tone="warn" title={t('adminTesting.revenueCheck.findingMissingDays', { count: agg.missingDays.length })}>
          {agg.missingDays.map((d, i) => (
            <li key={i} className={cn(secondaryText, 'text-xs')}>
              {t('adminTesting.revenueCheck.aggMissingDay', { restaurant: d.restaurantName, days: d.dates.length, dates: d.dates.join(', ') })}
            </li>
          ))}
        </TrivecFindingBlock>
      )}

      {agg.missingSections.length > 0 && (
        <TrivecFindingBlock tone="warn" title={t('adminTesting.revenueCheck.findingMissingSections', { count: agg.missingSections.length })}>
          {agg.missingSections.map((m, i) => (
            <li key={i} className={cn(secondaryText, 'text-xs')}>
              {t('adminTesting.revenueCheck.aggMissingSection', { restaurant: m.restaurantName, section: m.section, days: m.dates.length, dates: m.dates.join(', '), value: eur(m.totalValue) })}
            </li>
          ))}
        </TrivecFindingBlock>
      )}

      {agg.valueMismatches.length > 0 && (
        <TrivecFindingBlock tone="error" title={t(overridden ? 'adminTesting.revenueCheck.findingValueMismatchesApplied' : 'adminTesting.revenueCheck.findingValueMismatches', { count: agg.valueMismatches.length })}>
          {agg.valueMismatches.map((v, i) => (
            <li key={i} className={cn(secondaryText, 'text-xs')}>
              {t('adminTesting.revenueCheck.aggValueMismatch', { restaurant: v.restaurantName, section: v.section, occurrences: v.occurrences, stored: eur(v.totalStored), report: eur(v.totalReport) })}
              {v.totalStoredQuantity !== undefined && v.totalStoredQuantity !== v.totalReportQuantity && (
                <> · {t('adminTesting.revenueCheck.unitsChange', { stored: v.totalStoredQuantity, report: v.totalReportQuantity })}</>
              )}
            </li>
          ))}
        </TrivecFindingBlock>
      )}

      {agg.unconfiguredSections.length > 0 && (
        <TrivecFindingBlock tone="info" title={t('adminTesting.revenueCheck.findingUnconfiguredSections', { count: agg.unconfiguredSections.length })}>
          {agg.unconfiguredSections.map((u, i) => (
            <li key={i} className={cn(secondaryText, 'text-xs')}>
              {t('adminTesting.revenueCheck.aggUnconfiguredSection', { restaurant: u.restaurantName, section: u.section, days: u.days, value: eur(u.totalValue) })}
            </li>
          ))}
        </TrivecFindingBlock>
      )}
    </div>
  );
}

/** Summary line + aggregate worklist + (collapsed) per-email findings + apply results + errors. */
export function TrivecRevenueCheckResults({ result, t }: { result: RevenueCheckResult; t: TFn }) {
  const eur = (n: number) => `€${n.toFixed(2)}`;
  const [showDetails, setShowDetails] = useState(false);
  const { totals } = result;
  // Mismatches are only written when an apply run opted into overriding them; otherwise they are
  // listed for information and the headings must not imply anything was changed.
  const wroteMismatches = result.mode === 'apply' && result.overrideMismatches;
  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <span className={cn('px-2 py-0.5 rounded-full font-semibold',
            result.mode === 'apply'
              ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300'
              : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300')}>
            {result.mode === 'apply' ? t('adminTesting.revenueCheck.modeApply') : t('adminTesting.revenueCheck.modeDry')}
          </span>
          <span className={secondaryText}>
            {t('adminTesting.revenueCheck.summary', {
              listed: result.emailsListed,
              qualified: result.emailsQualified,
              filtered: result.emailsFilteredOut,
            })}
          </span>
        </div>
        <div className="flex flex-wrap gap-2">
          <TrivecSummaryChip label={t('adminTesting.revenueCheck.totalUnconfiguredRestaurants')} value={totals.unconfiguredRestaurants} />
          <TrivecSummaryChip label={t('adminTesting.revenueCheck.totalMissingDays')} value={totals.missingDays} />
          <TrivecSummaryChip label={t('adminTesting.revenueCheck.totalRowsWithMissingSections')} value={totals.rowsWithMissingSections} />
          <TrivecSummaryChip label={t('adminTesting.revenueCheck.totalValueMismatches')} value={totals.valueMismatches} />
          <TrivecSummaryChip label={t('adminTesting.revenueCheck.totalUnconfiguredSections')} value={totals.unconfiguredSections} />
        </div>
      </div>

      {/* Aggregate worklist first — the primary view. */}
      {result.findings.length === 0 ? (
        <div className={cn(secondaryText, smallTextSize, 'text-center py-4')}>{t('adminTesting.revenueCheck.noFindings')}</div>
      ) : (
        <>
          <TrivecAggregateView agg={result.aggregate} t={t} eur={eur} overridden={wroteMismatches} />

          {/* Per-email finding cards, collapsed by default. */}
          <div>
            <button
              onClick={() => setShowDetails((s) => !s)}
              className={cn('inline-flex items-center gap-1.5', secondaryText, 'text-xs', semiBoldText, 'hover:opacity-80')}
            >
              {showDetails ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
              {t('adminTesting.revenueCheck.perEmailToggle', { count: result.findings.length })}
            </button>
            {showDetails && (
              <div className="mt-2 space-y-3">
                {result.findings.map((f) => (
                  <TrivecFindingCard key={f.rfc822MessageId} finding={f} t={t} eur={eur} overridden={wroteMismatches} />
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {result.applied && (
        <div className="space-y-2 rounded-lg border border-green-200 dark:border-green-900/40 bg-green-50 dark:bg-green-900/10 p-3">
          <h4 className={cn(primaryText, semiBoldText, smallTextSize)}>{t('adminTesting.revenueCheck.appliedTitle')}</h4>
          <p className={cn(secondaryText, 'text-xs')}>
            {/* Each entry is ONE revenue_history row carrying several sections, so both are shown —
                the row count alone reads as far fewer sections than were actually written. */}
            {t('adminTesting.revenueCheck.appliedSummary', {
              inserted: result.applied.insertedDays.length,
              merged: result.applied.mergedSections.reduce((n, m) => n + m.sections.length, 0),
              mergedRows: result.applied.mergedSections.length,
              overridden: result.applied.overriddenSections.reduce((n, o) => n + o.sections.length, 0),
              overriddenRows: result.applied.overriddenSections.length,
              reaudited: result.applied.reauditedEmails,
            })}
          </p>
          {result.applied.insertedDays.length > 0 && (
            <ul className="space-y-0.5 pl-3">
              {result.applied.insertedDays.map((d, i) => (
                <li key={i} className={cn(secondaryText, 'text-xs')}>
                  {t('adminTesting.revenueCheck.appliedInsertedRow', { restaurant: d.restaurantName, date: d.date })}
                </li>
              ))}
            </ul>
          )}
          {result.applied.mergedSections.length > 0 && (
            <ul className="space-y-0.5 pl-3">
              {result.applied.mergedSections.map((m, i) => (
                <li key={i} className={cn(secondaryText, 'text-xs')}>
                  {t('adminTesting.revenueCheck.appliedMergedRow', { restaurant: m.restaurantName, date: m.date, sections: m.sections.join(', ') })}
                </li>
              ))}
            </ul>
          )}
          {result.applied.overriddenSections.length > 0 && (
            <ul className="space-y-0.5 pl-3">
              {result.applied.overriddenSections.map((o, i) => (
                <li key={i} className={cn(secondaryText, 'text-xs')}>
                  {t('adminTesting.revenueCheck.appliedOverriddenRow', { restaurant: o.restaurantName, date: o.date, sections: o.sections.join(', ') })}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {result.errors.length > 0 && (
        <div className="space-y-1 rounded-lg border border-red-200 dark:border-red-900/40 bg-red-50 dark:bg-red-900/10 p-3">
          <h4 className={cn(primaryText, semiBoldText, smallTextSize)}>{t('adminTesting.revenueCheck.errorsTitle', { count: result.errors.length })}</h4>
          <ul className="space-y-0.5 pl-3">
            {result.errors.map((e, i) => (
              <li key={i} className="text-xs text-red-700 dark:text-red-300">
                {e.subject ? `${e.subject}: ` : ''}{e.error}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
